'use client'
import Link from 'next/link'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/free-mode'
import { FreeMode } from 'swiper/modules'
import { useRef } from 'react'
import styles from './CustomPictureNav.module.css'

const resolveHref = (node) => {
  if (!node?.IsInternalURL) {
    return node?.ExternalURL?.trim() || '#'
  }

  if (!node?.LinkURL) return '#'

  const link = node.LinkURL.trim()
  const para = node.LinkURLPagePara?.trim() ?? ''

  // 首頁
  if (link === '/') return '/'

  // 判斷是否為「詳情頁」
  const isDetail =
    link.includes('Detail') ||
    link.includes('DetailPage') ||
    link.includes('Detail/') ||
    link.startsWith('/product/') // 商品詳情

  // 判斷是否為「列表頁」
  const isList =
    link.includes('List') ||
    link.includes('list') ||
    link.startsWith('/product-list')

  const isProductListCategory = link.startsWith('/product-list?categoryid=')
  const isProductListBrand = link.startsWith('/product-list?brandid=')
  const isMemberCenter = link.startsWith('/MemberCenter')

  let finalLink = ''

  if (isDetail) {
    finalLink = link.endsWith('/') ? `${link}${para}` : `/${link}${para}`
  } else if (isList) {
    if (isProductListCategory || isProductListBrand) {
      finalLink = `${link}${para}`
    } else {
      finalLink = link
    }
  } else if (isMemberCenter) {
    finalLink = `${link}${para}`
  } else {
    finalLink = link.startsWith('/') ? `${link}` : `/${link}`
  }

  finalLink = finalLink.replace(/\/{2,}/g, '/')

  return finalLink
}

export default function CustomPictureNav({
  cusomNav = [],
  headerStyle,
  activeCustomMenuId,
  onActivate,
}) {
  const headerFontColor =
    headerStyle?.HeaderFontColor && headerStyle?.HeaderFontColor != ''
      ? headerStyle.HeaderFontColor
      : '#fff'
  const headerFontFocusColor =
    headerStyle?.HeaderFontFocusColor && headerStyle?.HeaderFontFocusColor != ''
      ? headerStyle.HeaderFontFocusColor
      : 'red'
  return (
    <>
      {cusomNav
        .filter((item) => !item.ParentID && item.Active)
        .map((parent) => {
          const swiperRef = useRef(null)
          const total = parent.Children?.length || 0

          return (
            <li
              key={parent.MenuID}
              className="navigation__item"
              onMouseLeave={(e) => {
                e.currentTarget.classList.remove('force-close')
              }}
            >
              <span
                className="navigation__link"
                style={{
                  color:
                    activeCustomMenuId === parent.MenuID
                      ? headerFontFocusColor
                      : headerFontColor,
                }}
              >
                {parent.MenuName}
              </span>

              {total > 0 && (
                <div
                  className={`mega-menu ${styles.megaMenu}`}
                  style={{
                    display: 'flex', // 確保大容器使用 flex 排版
                    alignItems: 'center', // 🔥 核心修正：讓左右按鈕跟中間卡片強迫垂直置中
                    justifyContent: 'center', // 水平居中
                    width: '100%',
                    padding: '15px 0', // 給點上下間距，視覺更舒服
                  }}
                >
                  {/* 左側按鈕 */}
                  {total > 5 && (
                    <div
                      style={{
                        width: '60px',
                        flexShrink: 0, // 🔥 核心修正：防止圓形按鈕寬度被捏扁
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <button
                        onClick={() => swiperRef.current?.slidePrev()}
                        style={btnStyle}
                      >
                        ‹
                      </button>
                    </div>
                  )}

                  {/* 中間區域 */}
                  <div
                    style={{
                      flex: 1,
                      overflow: 'visible',
                      maxWidth: total > 5 ? 'calc(100% - 120px)' : '100%',
                    }}
                  >
                    {total <= 5 ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '20px',
                          flexWrap: 'wrap',
                          padding: '8px',
                        }}
                      >
                        {parent.Children.map((child) => (
                          <Card
                            key={child.MenuID}
                            child={child}
                            width={250}
                            parentMenuId={parent.MenuID}
                            onActivate={onActivate}
                          />
                        ))}
                      </div>
                    ) : (
                      <Swiper
                        modules={[FreeMode]}
                        onSwiper={(swiper) => {
                          swiperRef.current = swiper
                          swiper.slideTo(0, 0)
                        }}
                        slidesPerView="auto"
                        spaceBetween={20}
                        freeMode
                        grabCursor
                        watchOverflow={true}
                        style={{ width: '100%', overflow: 'hidden' }}
                        onResize={(swiper) => swiper.update()}
                      >
                        {parent.Children.map((child) => (
                          <SwiperSlide
                            key={child.MenuID}
                            style={{
                              width: '250px',
                              overflow: 'visible',
                              position: 'relative',
                              zIndex: 10,
                              paddingBottom: '5px',
                            }}
                          >
                            <Card
                              child={child}
                              width={250}
                              parentMenuId={parent.MenuID}
                              onActivate={onActivate}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    )}
                  </div>

                  {/* 右側按鈕 */}
                  {total > 5 && (
                    <div
                      style={{
                        width: '60px',
                        flexShrink: 0, // 🔥 核心修正：防止圓形按鈕寬度被捏扁
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <button
                        onClick={() => swiperRef.current?.slideNext()}
                        style={btnStyle}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        })}
    </>
  )
}

/* 單張卡片 */
function Card({ child, width, onActivate, parentMenuId }) {
  return (
    <Link
      href={resolveHref(child)}
      style={{
        display: 'block',
        background: '#fff',
        textAlign: 'center',
        padding: '16px',
        borderRadius: '10px',
        transition: 'box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none'
      }}
      onClickCapture={(e) => {
        const navItem = e.currentTarget.closest('.navigation__item')
        if (navItem) {
          navItem.classList.add('force-close')
        }
        onActivate?.(parentMenuId)
      }}
    >
      {child.PicturePath && (
        <div
          className="image-container"
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px',
            marginBottom: '12px',
            height: '250px',
            background: '#fff',
          }}
        >
          <Image
            src={child.PicturePath}
            alt={child.MenuName}
            width={width}
            height={250}
            style={{
              objectFit: 'cover',
              borderRadius: '8px',
              transition: 'transform 0.4s ease',
              willChange: 'transform',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          />
        </div>
      )}

      <h4
        style={{
          fontSize: '15px',
          fontWeight: '500',
          marginBottom: '8px',
          whiteSpace: 'nowrap',
          color: '#333', // 確保文字在白色卡片上清晰可見
        }}
      >
        {child.MenuName}
      </h4>
    </Link>
  )
}

/* 左右按鈕樣式 */
const btnStyle = {
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: '50%',
  width: '36px',
  height: '36px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  color: '#333', // 箭頭顏色
}
